import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Tutti gli axios mandano i cookie httpOnly automaticamente (set globalmente in AuthContext.js).
// Non leggiamo più il token da localStorage: il JWT è ora in un cookie httpOnly inaccessibile a JS.

// Interceptor: normalizza FastAPI/Pydantic validation errors (422) in stringa leggibile.
// Senza questo, `error.response.data.detail` può essere un array di oggetti che React
// non sa renderizzare ("Objects are not valid as a React child").
// Solo per 422 con detail array: gli altri formati custom del backend rimangono intatti.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 422 && Array.isArray(detail)) {
      // Pydantic v2: ogni item ha { type, loc: [...], msg, input, ctx? }
      const isPydanticShape = detail.every(d => d && typeof d === 'object' && 'msg' in d);
      if (isPydanticShape) {
        const messages = detail.map((d) => {
          const loc = Array.isArray(d.loc) ? d.loc.filter(p => p !== 'body').join('.') : '';
          return loc ? `${loc}: ${d.msg || 'errore'}` : (d.msg || 'errore di validazione');
        });
        error.response.data.detail = messages.join(' · ');
      }
    }
    return Promise.reject(error);
  },
);

// API Auth
export const authApi = {
  login: (credentials) => axios.post(`${API_URL}/auth/login`, credentials),
  register: (userData) => axios.post(`${API_URL}/auth/register`, userData),
  getMe: () => axios.get(`${API_URL}/auth/me`),
};

// API Users (Simplified)
export const usersApi = {
  getAll: (tipo) => axios.get(`${API_URL}/users`, { params: { tipo } }),
  getById: (id) => axios.get(`${API_URL}/users/${id}`),
  create: (data) => axios.post(`${API_URL}/users`, data),
  update: (id, data) => axios.put(`${API_URL}/users/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/users/${id}`),
  sendWelcomeEmail: (id) => axios.post(`${API_URL}/users/${id}/send-welcome-email`),
  deactivate: (id) => axios.post(`${API_URL}/users/${id}/deactivate`),
  reactivate: (id) => axios.post(`${API_URL}/users/${id}/reactivate`),
};

export const adminsApi = {
  create: (data) => axios.post(`${API_URL}/admins`, data),
  update: (id, data) => axios.put(`${API_URL}/admins/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/admins/${id}`),
};

// API Properties (Simplified)
export const propertiesApi = {
  getAll: () => axios.get(`${API_URL}/properties`),
  getById: (id) => axios.get(`${API_URL}/properties/${id}`),
  create: (data) => axios.post(`${API_URL}/properties`, data),
  update: (id, data) => axios.put(`${API_URL}/properties/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/properties/${id}`),
};

// API Contracts (Extended)
export const contractsApi = {
  getAll: () => axios.get(`${API_URL}/contracts`),
  getById: (id) => axios.get(`${API_URL}/contracts/${id}`),
  create: (data) => axios.post(`${API_URL}/contracts`, data),
  update: (id, data) => axios.put(`${API_URL}/contracts/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/contracts/${id}`),
  // Subentro
  registraSubentro: (id, data) => axios.post(`${API_URL}/contracts/${id}/subentro`, data),
  // Documenti contratto
  getDocumenti: (id) => axios.get(`${API_URL}/contracts/${id}/documenti`),
  aggiungiDocumento: (id, data) => axios.post(`${API_URL}/contracts/${id}/documenti`, data),
  eliminaDocumento: (contractId, docId) => axios.delete(`${API_URL}/contracts/${contractId}/documenti/${docId}`),
  downloadDocumento: (contractId, docId) => axios.get(`${API_URL}/contracts/${contractId}/documenti/${docId}/download`, { responseType: 'blob' }),
  // Generazione mandato/contratto servizio
  generaMandatoServizio: (id) => axios.post(`${API_URL}/contracts/${id}/genera-mandato-servizio`),
  generaContrattoServizio: (id) => axios.post(`${API_URL}/contracts/${id}/genera-contratto-servizio`),
  getMandatoServizio: (id) => axios.get(`${API_URL}/contracts/${id}/mandato-servizio`),
  getContrattoServizio: (id) => axios.get(`${API_URL}/contracts/${id}/contratto-servizio`),
  // Download DOCX compilati
  downloadMandatoDocx: (id) => `${API_URL}/contracts/${id}/mandato-servizio/download`,
  downloadContrattoServizioDocx: (id) => `${API_URL}/contracts/${id}/contratto-servizio/download`,
  // Cessazione contratto con data specifica
  cessa: (id, data) => axios.post(`${API_URL}/contracts/${id}/cessa`, data),
};

// API Tipi documento
export const tipiDocumentoApi = {
  getAll: () => axios.get(`${API_URL}/tipi-documento-contratto`),
};

// API Mandati/Contratti Servizio
export const mandatiApi = {
  upload: (codice, data) => axios.post(`${API_URL}/mandati-servizio/${codice}/upload`, data),
};

export const contrattiServizioApi = {
  upload: (codice, data) => axios.post(`${API_URL}/contratti-servizio/${codice}/upload`, data),
};

// API Payments (Simplified)
export const paymentsApi = {
  getAll: (params) => axios.get(`${API_URL}/payments`, { params }),
  getById: (id) => axios.get(`${API_URL}/payments/${id}`),
  create: (data) => axios.post(`${API_URL}/payments`, data),
  update: (id, data) => axios.put(`${API_URL}/payments/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/payments/${id}`),
  generateMonthly: (month) => axios.post(`${API_URL}/payments/generate-monthly`, null, { params: { month } }),
  bulkUpdateStatus: (data) => axios.post(`${API_URL}/payments/bulk-update-status`, data),
  startFatturazioneMassiva: (data) => axios.post(`${API_URL}/payments/fatturazione-massiva/start`, data),
  statoFatturazioneMassiva: (jobId) => axios.get(`${API_URL}/payments/fatturazione-massiva/${jobId}`),
  aggiornaStatiSdi: (data) => axios.post(`${API_URL}/payments/aggiorna-stati-sdi`, data || {}),
  generateXML: (data) => axios.post(`${API_URL}/generate-xml`, data, {
    responseType: 'blob'
  }),
  // Nuove API per azioni pagamenti aperti
  confermaIncasso: (data) => axios.post(`${API_URL}/payments/conferma-incasso`, data),
  sollecita: (data) => axios.post(`${API_URL}/payments/sollecita`, data),
  exportData: (format, params) => axios.get(`${API_URL}/export/payments/${format}`, {
    params,
    responseType: 'blob'
  }),
};

// API XML Generation
export const xmlApi = {
  generate: (data) => axios.post(`${API_URL}/generate-xml`, data, {
    responseType: 'blob'
  }),
};

// API Dashboard Stats
export const statsApi = {
  getDashboard: () => axios.get(`${API_URL}/stats/dashboard`),
  exportRecap: (formato) => axios.get(`${API_URL}/stats/dashboard/export?formato=${formato}`, { responseType: 'blob' }),
};

// API Avvisi di sicurezza (banner dashboard admin)
export const securityApi = {
  getAlerts: () => axios.get(`${API_URL}/admin/security-alerts`),
  markSeen: () => axios.post(`${API_URL}/admin/security-alerts/seen`),
};

// Stub APIs for pages not yet implemented
export const documentiApi = {
  getAll: () => Promise.resolve({ data: [] }),
};

export const documentiUtenteApi = {
  getAll: (userId) => axios.get(`${API_URL}/users/${userId}/documenti`),
  upload: (formData) => axios.post(`${API_URL}/users/${formData.get('user_id')}/documenti`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (userId, docId) => axios.get(`${API_URL}/users/${userId}/documenti/${docId}/download`, {
    responseType: 'blob'
  }),
  delete: (userId, docId) => axios.delete(`${API_URL}/users/${userId}/documenti/${docId}`),
};

export const documentiImmobileApi = {
  getAll: (propertyId) => axios.get(`${API_URL}/properties/${propertyId}/documenti`),
  upload: (propertyId, formData) => axios.post(`${API_URL}/properties/${propertyId}/documenti`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (propertyId, docId) => axios.get(`${API_URL}/properties/${propertyId}/documenti/${docId}/download`, {
    responseType: 'blob'
  }),
  delete: (propertyId, docId) => axios.delete(`${API_URL}/properties/${propertyId}/documenti/${docId}`),
  getTipi: () => axios.get(`${API_URL}/tipi-documento-immobile`),
};

export const sollecitiApi = {
  getAll: (params) => axios.get(`${API_URL}/solleciti`, { params }),
  getByContratto: (contrattoId) => axios.get(`${API_URL}/solleciti/per-contratto/${contrattoId}`),
  create: (data) => axios.post(`${API_URL}/solleciti`, data),
  updateStato: (id, stato, note) => axios.put(`${API_URL}/solleciti/${id}/stato`, { stato, note }),
  delete: (id) => axios.delete(`${API_URL}/solleciti/${id}`),
};

export const distinteSddApi = {
  getAll: () => Promise.resolve({ data: [] }),
  create: () => Promise.resolve({ data: {} }),
  delete: () => Promise.resolve({ data: {} }),
};

// API Pagamenti Straordinari
export const pagamentiStraordinariApi = {
  getAll: (params) => axios.get(`${API_URL}/pagamenti-straordinari`, { params }),
  getById: (id) => axios.get(`${API_URL}/pagamenti-straordinari/${id}`),
  create: (data) => axios.post(`${API_URL}/pagamenti-straordinari`, data),
  update: (id, data) => axios.put(`${API_URL}/pagamenti-straordinari/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/pagamenti-straordinari/${id}`),
  getTipi: () => axios.get(`${API_URL}/tipi-straordinario`),
};

// User Portal API (per portale utente e modalità Ghost admin)
export const userPortalApi = {
  getData: (userId) => {
    const params = userId ? { user_id: userId } : {};
    return axios.get(`${API_URL}/user/portal-data`, { params });
  },
};

// Legacy compatibility (mantengo per non rompere le vecchie pagine)
export const immobiliApi = propertiesApi;
export const contrattiApi = contractsApi;
export const pagamentiApi = paymentsApi;
